import React, { useEffect, useState } from 'react';
import { Button, Descriptions, Drawer, Image, Spin, Tabs, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PictureOutlined, LinkOutlined } from '@ant-design/icons';
import { watchesAPI } from '../../../Utils/api';
import { MobileWatch, STATUS_COLORS, STATUS_LABELS, formatPrice } from './productStatus';
import { SheetFooter } from './BottomSheet.style';
import { Head, HeadImage, HeadInfo, CategoryGroup, ImageGrid, Empty } from './ProductPreviewSheet.style';

const API_URL = process.env.REACT_APP_API_URL || '';
const FE_URL = process.env.REACT_APP_FE_URL || 'https://www.mr-luxury-watches.com';

const TAX_SCHEMES: Record<string, string> = {
  MARGIN_25A_USTG: 'Differenzbesteuerung gem. §25a UStG',
  STANDARD_VAT_19: 'Regelbesteuerung (inkl. 19% MwSt.)',
};

interface ProductPreviewSheetProps {
  watch: MobileWatch | null;
  onClose: () => void;
  onEdit: (watch: MobileWatch) => void;
  onDelete: (watch: MobileWatch) => void;
  onCopyShortLink: (watch: MobileWatch) => void;
}

// Read-only preview with the same tabs as the product edit page
const ProductPreviewSheet: React.FC<ProductPreviewSheetProps> = ({ watch, onClose, onEdit, onDelete, onCopyShortLink }) => {
  const [details, setDetails] = useState<any>(null);
  const [taxonomy, setTaxonomy] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!watch) return;
    let cancelled = false;
    setLoading(true);
    setDetails(null);
    setTaxonomy([]);
    Promise.all([watchesAPI.getWatch(watch.id), watchesAPI.getTaxonomy(watch.id)])
      .then(([watchRes, taxRes]) => {
        if (cancelled) return;
        setDetails(watchRes.data.watch);
        setTaxonomy(taxRes.data.taxonomy || []);
      })
      .catch(() => { /* header data from the list stays visible */ })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [watch]);

  const data = { ...watch, ...details };
  const images: any[] = [...(details?.images || watch?.images || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const yesNo = (value?: boolean) => (value ? 'Ja' : 'Nein');

  const groups = taxonomy.reduce((acc: Record<string, { name: string; order: number; values: string[] }>, item: any) => {
    const category = item.option?.category;
    if (!category) return acc;
    acc[category.id] = acc[category.id] || { name: category.name, order: category.displayOrder ?? 0, values: [] };
    acc[category.id].values.push(item.option.value);
    return acc;
  }, {});

  const general = (
    <Descriptions
      column={1}
      size="small"
      bordered={false}
      styles={{ label: { width: 130, color: '#595959' } }}
      items={[
        { key: 'brand', label: 'Marke', children: data.brand },
        { key: 'model', label: 'Modell', children: data.model },
        { key: 'price', label: 'Preis (EUR)', children: formatPrice(data.priceCents) },
        { key: 'status', label: 'Status', children: <Tag color={STATUS_COLORS[data.status]}>{STATUS_LABELS[data.status] || data.status}</Tag> },
        { key: 'sku', label: 'SKU', children: data.sku || '—' },
        { key: 'ref', label: 'Referenznummer', children: data.referenceNumber || '—' },
        {
          key: 'short',
          label: 'Short-Link',
          children: data.shortCode ? (
            <span style={{ wordBreak: 'break-all' }}>
              <a href={`${FE_URL}/s/${data.shortCode}`} target="_blank" rel="noreferrer">/s/{data.shortCode}</a>
              <Button type="text" size="small" icon={<LinkOutlined />} onClick={() => watch && onCopyShortLink(watch)} aria-label="Short-Link kopieren" />
            </span>
          ) : '—',
        },
        { key: 'year', label: 'Produktionsjahr', children: data.yearOfProduction || '—' },
        { key: 'tax', label: 'Besteuerung', children: TAX_SCHEMES[data.taxScheme] || data.taxScheme || '—' },
        { key: 'box', label: 'Box vorhanden', children: yesNo(data.hasBox) },
        { key: 'papers', label: 'Papiere vorhanden', children: yesNo(data.hasPapers) },
      ]}
    />
  );

  const categories = Object.values(groups).length === 0 ? <Empty>Keine Filter / Kategorien hinterlegt</Empty> : (
    Object.values(groups).sort((a, b) => a.order - b.order).map((group) => (
      <CategoryGroup key={group.name}>
        <div className="name">{group.name}</div>
        {group.values.map((value) => <Tag key={value}>{value}</Tag>)}
      </CategoryGroup>
    ))
  );

  const gallery = images.length === 0 ? <Empty>Keine Bilder hinterlegt</Empty> : (
    <Image.PreviewGroup>
      <ImageGrid>
        {images.map((image) => (
          <Image key={image.id} src={`${API_URL}${image.thumbnailUrl || image.originalUrl}`} preview={{ src: `${API_URL}${image.originalUrl}` }} alt={image.altText || ''} />
        ))}
      </ImageGrid>
    </Image.PreviewGroup>
  );

  return (
    <Drawer
      placement="bottom"
      open={!!watch}
      onClose={onClose}
      // Fixed height so switching tabs does not make the sheet jump
      size="90vh"
      styles={{ wrapper: { borderRadius: '16px 16px 0 0', overflow: 'hidden' } }}
      footer={watch && (
        <SheetFooter>
          <Button type="primary" block icon={<EditOutlined />} onClick={() => onEdit(watch)} data-testid="mobile-preview-edit">Bearbeiten</Button>
          <Button danger block icon={<DeleteOutlined />} onClick={() => onDelete(watch)} data-testid="mobile-preview-delete">Löschen</Button>
        </SheetFooter>
      )}
    >
      {watch && (
        <div data-testid="mobile-preview">
          <Head>
            <HeadImage>
              {images[0] ? <img src={`${API_URL}${images[0].thumbnailUrl || images[0].originalUrl}`} alt="" /> : <PictureOutlined />}
            </HeadImage>
            <HeadInfo>
              <span className="brand">{data.brand}</span>
              <span className="model">{data.model}</span>
              <Tag color={STATUS_COLORS[data.status]}>{STATUS_LABELS[data.status] || data.status}</Tag>
              <span className="price">{formatPrice(data.priceCents)}</span>
            </HeadInfo>
          </Head>
          {loading && !details ? <Empty><Spin /></Empty> : (
            <Tabs
              items={[
                { key: 'general', label: 'Allgemeine Daten', children: general },
                { key: 'taxonomy', label: 'Filter / Kategorien', children: categories },
                { key: 'images', label: 'Bilder', children: gallery },
              ]}
            />
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ProductPreviewSheet;
